import React, { useState } from 'react';
import { X, Zap, Check, Loader } from 'lucide-react';
import { apiPost } from '../lib/api';

interface Props {
  onClose: () => void;
}

export default function PaywallModal({ onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpgrade = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiPost<{ url: string }>('/api/stripe/create-checkout', {});
      window.location.href = data.url;
    } catch (err: unknown) {
      setError((err as Error).message || 'Erreur lors de la création du paiement');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white relative">
          <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-lg">
              <Zap className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold">Passez Premium</h2>
          </div>
          <p className="text-purple-100 text-sm">
            Vous avez atteint la limite de 2 fiches gratuites.
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="text-4xl font-bold text-gray-900">2€</div>
            <div className="text-gray-500 text-sm">par mois</div>
          </div>

          <ul className="space-y-3 mb-6">
            {[
              'Fiches personnages illimitées',
              'Sauvegarde cloud sécurisée',
              'Partage et publication',
              'Accès sur tous vos appareils',
              'Annulable à tout moment'
            ].map(feature => (
              <li key={feature} className="flex items-center gap-3 text-gray-700">
                <div className="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-green-600" />
                </div>
                {feature}
              </li>
            ))}
          </ul>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {loading ? (
              <><Loader className="w-4 h-4 animate-spin" /> Redirection vers Stripe...</>
            ) : (
              <><Zap className="w-4 h-4" /> Passer Premium — 2€/mois</>
            )}
          </button>

          <p className="text-center text-xs text-gray-400 mt-3">
            Paiement sécurisé par Stripe. Annulation possible à tout moment.
          </p>
        </div>
      </div>
    </div>
  );
}
